import React from 'react';
import { Box, Typography, Switch, FormControlLabel } from '@mui/material';
import { Pencil, Trash2, Gamepad2, Sparkles, Image as ImageIcon, GripVertical } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import styles from './GameComposer.module.scss';

export interface GameCategory {
  id: number | string;
  title: string;
  translations?: Record<string, { title: string }>;
  gameCount: number;
  showOnFirstPage: boolean;
  image?: string;
}

interface CategoryItemProps {
  category: GameCategory;
  onEdit: (cat: GameCategory) => void;
  onDelete: (id: number | string) => void;
  onManageGames: (cat: GameCategory) => void;
  onToggleShowOnFirstPage: (id: number | string) => void;
  onManageFirstPageGames: (cat: GameCategory) => void;
  onManageImage: (cat: GameCategory) => void;
}

const CategoryItem: React.FC<CategoryItemProps> = ({
  category,
  onEdit,
  onDelete,
  onManageGames,
  onToggleShowOnFirstPage,
  onManageFirstPageGames,
  onManageImage,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: category.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1000 : 1,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <Box ref={setNodeRef} style={style} className={styles.categoryCard}>
      <Box className={styles.info}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Box {...attributes} {...listeners} className={styles.dragHandle}>
            <GripVertical size={20} />
          </Box>
          <Typography className={styles.title}>{category.title}</Typography>
          {category.translations && Object.keys(category.translations).length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              {Object.keys(category.translations).map((lang) => (
                <Typography
                  key={lang}
                  component="span"
                  sx={{
                    fontSize: '10px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    px: '6px',
                    py: '1px',
                    borderRadius: '4px',
                    bgcolor: 'rgba(0, 179, 245, 0.12)',
                    color: 'var(--accent-blue)',
                    border: '1px solid rgba(0, 179, 245, 0.25)',
                    letterSpacing: '0.5px',
                  }}
                  title={`Translation for ${lang.toUpperCase()}: ${category.translations?.[lang]?.title}`}
                >
                  {lang}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
        <Typography className={styles.gameCount} sx={{ ml: 4 }}>
          Number of games: {category.gameCount}
        </Typography>
      </Box>

      <Box className={styles.actions}>
        <Box className={styles.deviceIcons}>
          <FormControlLabel
            control={
              <Switch
                checked={category.showOnFirstPage}
                onChange={() => onToggleShowOnFirstPage(category.id)}
                color="primary"
                size="small"
              />
            }
            label="Show on First Page"
            labelPlacement="start"
            sx={{
              margin: 0,
              '& .MuiFormControlLabel-label': {
                fontSize: '13px',
                color: 'var(--text-secondary)',
                marginRight: '5px',
              },
            }}
          />
        </Box>

        <Box className={styles.actionIcons}>
          <Box
            className={styles.iconBtn}
            onClick={() => onManageGames(category)}
            title="Manage Category Games"
          >
            <Gamepad2 size={20} />
          </Box>
          <Box
            className={styles.iconBtn}
            onClick={() => onManageFirstPageGames(category)}
            title="Manage Carousel Games"
          >
            <Sparkles size={18} />
          </Box>
          <Box
            className={styles.iconBtn}
            onClick={() => onEdit(category)}
            title="Edit Category Title"
          >
            <Pencil size={18} />
          </Box>
          <Box
            className={styles.iconBtn}
            onClick={() => onManageImage(category)}
            title="Edit Category Image"
          >
            <ImageIcon size={18} />
          </Box>
          <Box
            className={`${styles.iconBtn} ${styles.delete}`}
            onClick={() => onDelete(category.id)}
          >
            <Trash2 size={18} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CategoryItem;
